<template>
	<!-- Stays in the page, hidden: only the editor inside it moves into the drawing window, so Vue never needs it as an anchor -->
	<div class="drawing-host">
		<div ref="editorEl" class="drawing-editor" @contextmenu.prevent>
			<div class="drawing-toolbar">
				<div class="tool-group" role="radiogroup">
					<button type="button" class="tool" :class="{ selected: tool === 'pen' }" role="radio" :aria-checked="tool === 'pen'" :aria-label="t('mycalls.videoDrawingPen')" :title="t('mycalls.videoDrawingPen')" @click="pickTool('pen')">
						<i class="pi pi-pencil"></i>
					</button>
					<button type="button" class="tool" :class="{ selected: tool === 'text' }" role="radio" :aria-checked="tool === 'text'" :aria-label="t('mycalls.videoDrawingText')" :title="t('mycalls.videoDrawingText')" @click="pickTool('text')">
						<span class="text-glyph">T</span>
					</button>
				</div>

				<div class="tool-group" role="radiogroup" :aria-label="t('mycalls.videoDrawingColor')">
					<button v-for="(each, index) in DRAWING_COLORS" :key="each" type="button" class="swatch" :class="{ selected: color === each }" :style="{ background: each }" role="radio" :aria-checked="color === each" :aria-label="`${t('mycalls.videoDrawingColor')} ${index + 1}`" @click="pickColor(each)"></button>
				</div>

				<div class="tool-group" role="radiogroup" :aria-label="t('mycalls.videoDrawingThickness')">
					<button v-for="index in THICKNESSES" :key="index" type="button" class="tool" :class="{ selected: thickness === index - 1 }" role="radio" :aria-checked="thickness === index - 1" :aria-label="`${t('mycalls.videoDrawingThickness')} ${index}`" :title="`${t('mycalls.videoDrawingThickness')} ${index}`" @click="pickThickness(index - 1)">
						<span class="dot" :style="{ width: `${3 + (index * 3)}px`, height: `${3 + (index * 3)}px`, background: color }"></span>
					</button>
				</div>

				<div class="tool-group">
					<button type="button" class="tool" :disabled="!canUndo(marks)" :aria-label="t('mycalls.videoDrawingUndo')" :title="t('mycalls.videoDrawingUndo')" @click="takeBack()">
						<i class="pi pi-undo"></i>
					</button>
					<button type="button" class="tool" :disabled="marks.shapes.length === 0" :aria-label="t('mycalls.videoDrawingClear')" :title="t('mycalls.videoDrawingClear')" @click="clearAll()">
						<i class="pi pi-trash"></i>
					</button>
					<button type="button" class="tool" :aria-label="t('mycalls.videoDrawingSave')" :title="t('mycalls.videoDrawingSave')" @click="save()">
						<i class="pi pi-download"></i>
					</button>
				</div>

				<div class="hint">{{ t('mycalls.videoDrawingHint') }}</div>

				<Button size="small" severity="danger" class="end-button" :label="t('mycalls.videoDrawingEnd')" icon="pi pi-times" @click="close('the user ended it')" />
			</div>

			<div ref="stageEl" class="drawing-stage">
				<div class="paper" :style="paperStyle">
					<canvas ref="canvasEl" :class="tool" @pointerdown="pointerDown" @pointermove="pointerMove" @pointerup="pointerUp" @pointercancel="pointerUp" @mousedown.prevent></canvas>
					<input v-if="entry" :key="entry.id" ref="entryEl" v-model="entry.value" class="text-entry" :style="entryStyle" :data-entry="entry.id" :placeholder="t('mycalls.videoDrawingTextPlaceholder')" @keydown.enter.prevent="commitText(entry.id)" @keydown.esc.prevent="cancelText()" @blur="onEntryBlur">
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
/**
 * Drawing on a still of the other party's picture (VideoCallView opens it): the still opens in a window of its own, and
 * what is drawn on it is my video on the call in place of the camera until the drawing ends - closing the window, "End
 * drawing", or SipVideo ending it (hold, a change of other party, the call ending). The other party sees every line as it
 * is drawn.
 *
 * The editor is rendered here and moved into the window, as the video view moves into its pop-out window.
 */

import { computed, nextTick, onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVideoCallsStore } from '@/stores/VideoCalls'
import SipClient from '@/utilities/SipClient'
import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import { DRAWING_COLORS, THICKNESSES, addShape, canUndo, clearShapes, drawingSize, emptyMarks, paintDrawing, penWidth, snapshotFilename, textFont, textSize, toDrawing, undo, type Marks, type Point, type StrokeShape } from '@/utilities/video/drawing'
import { openPopout, shareStyles } from '@/utilities/video/popoutWindow'
import { saveCanvas } from '@/utilities/video/snapshot'

interface Props {
	/** The call the drawing is shown on - its SIP Call-ID */
	callId: string
	/** The still of the other party's picture */
	still: HTMLCanvasElement
	/** Who the other party is, for the window's title */
	partyLabel: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
	/** The drawing has ended and its window is closed */
	close: []
}>()

const { t } = useI18n()
const videoCalls = useVideoCallsStore()

/** The most frames a second the drawing sends - while lines are drawn */
const FRAME_RATE = 15

/** How often the drawing is painted again while nothing changes: a canvas that isn't painted sends no frames, and the other party's video would show as paused */
const KEEP_ALIVE_MS = 250

/** The toolbar's height, for the window's size */
const TOOLBAR_HEIGHT = 56

/** The drawing window's smallest size */
const MIN_WIDTH = 640
const MIN_HEIGHT = 420

const editorEl = ref<HTMLElement>()
const stageEl = ref<HTMLElement>()
const canvasEl = ref<HTMLCanvasElement>()
const entryEl = ref<HTMLInputElement>()

/** The drawing's size, in its own pixels */
const size = drawingSize(props.still.width, props.still.height)

/** The drawing window, once it is open */
const popout = shallowRef<Window>()

/** The text being typed: where it goes and how it looks */
const entry = ref<{ id: number, at: Point, value: string, color: string, size: number }>()

// #region Tools

const tool = ref<'pen' | 'text'>('pen')
const color = ref<string>(DRAWING_COLORS[0])
const thickness = ref(1)

/**
 * Choose the pen or the text tool
 * @param which The tool
 */
function pickTool(which: 'pen' | 'text') {
	commitPending()
	tool.value = which
}

/**
 * Choose the colour - of the text being typed too
 * @param which The colour
 */
function pickColor(which: string) {
	color.value = which
	if (entry.value) { entry.value.color = which }
	focusEntry()
}

/**
 * Choose the thickness - the pen's width, the text's height - of the text being typed too
 * @param which 0 (thinnest) to THICKNESSES - 1
 */
function pickThickness(which: number) {
	thickness.value = which
	if (entry.value) { entry.value.size = textSize(which, size) }
	focusEntry()
}

// #endregion

// #region Painting

const marks = shallowRef<Marks>(emptyMarks())

/** The line being drawn, until the pointer lets go */
let stroke: { pointerId: number, shape: StrokeShape } | undefined

let ctx: CanvasRenderingContext2D | null = null
let paintRequested = false

/** Paint the still and the marks - the line being drawn too */
function paint() {
	if (!ctx) { return }
	paintDrawing(ctx, props.still, size, stroke ? [ ...marks.value.shapes, stroke.shape ] : marks.value.shapes)
}

/** Paint at the window's next frame - at most once a frame however fast the pointer moves */
function paintSoon() {
	if (paintRequested) { return }

	const win = popout.value ?? window

	paintRequested = true
	win.requestAnimationFrame(() => {
		paintRequested = false
		paint()
	})
}

watch(marks, paint)

function takeBack() {
	cancelText()
	marks.value = undo(marks.value)
}

function clearAll() {
	cancelText()
	marks.value = clearShapes(marks.value)
}

/** Save the drawing as it is now, as a picture file */
async function save() {
	commitPending()
	paint()
	if (canvasEl.value) { await saveCanvas(canvasEl.value, snapshotFilename(t('mycalls.videoDrawingFile'), new Date())) }
}

// #endregion

// #region Pointer and text

/**
 * Where the pointer is on the drawing
 * @param event A pointer event on the canvas
 */
function pointAt(event: PointerEvent): Point | undefined {
	const canvas = canvasEl.value

	return canvas ? toDrawing(event.clientX, event.clientY, canvas.getBoundingClientRect(), size) : undefined
}

/**
 * Start a line, or place a text
 * @param event The pointerdown event
 */
function pointerDown(event: PointerEvent) {
	if (event.button !== 0 || stroke) { return }

	const at = pointAt(event)

	if (!at) { return }
	if (tool.value === 'text') {
		placeText(at)
		return
	}

	commitPending()

	const canvas = canvasEl.value

	// The line goes on when the pointer leaves the canvas, and ends wherever it lets go
	if (canvas && typeof canvas.setPointerCapture === 'function') { canvas.setPointerCapture(event.pointerId) }
	stroke = { pointerId: event.pointerId, shape: { kind: 'stroke', color: color.value, width: penWidth(thickness.value, size), points: [ at ] } }
	paintSoon()
}

/**
 * Draw the line on
 * @param event The pointermove event
 */
function pointerMove(event: PointerEvent) {
	if (!stroke || event.pointerId !== stroke.pointerId) { return }

	// Every point the pointer passed since the last event, so a quick line stays smooth
	const passed = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : []

	for (const each of passed.length > 0 ? passed : [ event ]) {
		const at = pointAt(each)

		if (at) { stroke.shape.points.push(at) }
	}
	paintSoon()
}

/**
 * Finish the line
 * @param event The pointerup or pointercancel event
 */
function pointerUp(event: PointerEvent) {
	if (!stroke || event.pointerId !== stroke.pointerId) { return }

	const shape = stroke.shape

	stroke = undefined
	marks.value = addShape(marks.value, shape)
}

let entries = 0

/** How many pixels on screen a pixel of the drawing takes up */
const scale = ref(1)

const paperStyle = computed(() => ({ width: `${size.width * scale.value}px`, height: `${size.height * scale.value}px` }))

const entryStyle = computed(() => {
	const text = entry.value

	if (!text) { return {} }
	return {
		left: `${text.at.x * scale.value}px`,
		top: `${text.at.y * scale.value}px`,
		font: textFont(text.size * scale.value),
		color: text.color
	}
})

/**
 * Start typing a text where the drawing was clicked - a text being typed is placed first
 * @param at Where it goes
 */
function placeText(at: Point) {
	commitPending()
	entry.value = { id: ++entries, at, value: '', color: color.value, size: textSize(thickness.value, size) }
	focusEntry()
}

/** Put the cursor back in the text being typed - after a click on the toolbar */
function focusEntry() {
	void nextTick(() => { entryEl.value?.focus() })
}

/**
 * Place the text typed - an empty one is dropped
 * @param id Which text: a blur arriving for one already placed or dropped changes nothing
 */
function commitText(id: number) {
	const text = entry.value

	if (!text || text.id !== id) { return }
	entry.value = undefined

	const value = text.value.trim()

	if (value) { marks.value = addShape(marks.value, { kind: 'text', color: text.color, size: text.size, at: text.at, text: value }) }
}

/**
 * Place the text when its field loses the cursor - the field may be on its way out already, so it says which text it was
 * @param event The blur event
 */
function onEntryBlur(event: FocusEvent) {
	commitText(Number((event.target as HTMLInputElement).dataset.entry))
}

function cancelText() {
	entry.value = undefined
}

/** Place the text being typed, if any */
function commitPending() {
	if (entry.value) { commitText(entry.value.id) }
}

/**
 * Undo with Ctrl/Cmd+Z - not while typing, where it undoes the typing
 * @param event The keydown event in the drawing window
 */
function onKey(event: KeyboardEvent) {
	if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !entry.value) {
		event.preventDefault()
		takeBack()
	}
}

// #endregion

// #region The window and the call

let stopSharingStyles: (() => void) | undefined
let keepAlive: number | undefined
let sharing: Promise<boolean> | undefined
let closed = false

/** Fit the drawing in the window, whole */
function fitToWindow() {
	const stage = stageEl.value

	if (!stage || stage.clientWidth === 0 || stage.clientHeight === 0) { return }
	scale.value = Math.min(stage.clientWidth / size.width, stage.clientHeight / size.height)
}

/** The window's size: the drawing at its own size, made smaller to fit in most of the screen */
function windowSize() {
	const room = { width: screen.availWidth * 0.85, height: (screen.availHeight * 0.85) - TOOLBAR_HEIGHT }
	const fit = Math.min(1, room.width / size.width, room.height / size.height)

	return { width: Math.round(size.width * fit), height: Math.round(size.height * fit) + TOOLBAR_HEIGHT }
}

/**
 * End the drawing: the camera comes back if it was on before, the window closes
 * @param why For the log
 */
function close(why: string) {
	if (closed) { return }
	closed = true
	logger.info(`DrawingEditor: The drawing on call ${props.callId} ends - ${why}`)

	const win = popout.value

	if (keepAlive !== undefined) { win?.clearInterval(keepAlive) }
	keepAlive = undefined
	win?.removeEventListener('pagehide', onWindowClosed)
	win?.removeEventListener('resize', fitToWindow)
	win?.removeEventListener('keydown', onKey)
	window.removeEventListener('pagehide', closeWindow)

	// Still on its way onto the call: end it once it is there - only this drawing, never one started after it
	if (sharing) {
		void sharing.then(shown => (shown ? SipClient.instance.stopDrawing(props.callId) : undefined)).catch(() => { /* start() reports it */ })
	}
	void SipClient.instance.stopDrawing(props.callId)

	closeWindow()
	emit('close')
}

function closeWindow() {
	stopSharingStyles?.()
	stopSharingStyles = undefined
	if (popout.value && !popout.value.closed) { popout.value.close() }
}

function onWindowClosed() {
	close('the window was closed')
}

/** Open the window, move the editor into it and put the drawing on the call */
async function start() {
	const canvas = canvasEl.value
	const editor = editorEl.value

	if (!canvas || !editor) { return }

	canvas.width = size.width
	canvas.height = size.height
	ctx = canvas.getContext('2d')
	paint()

	let win: Window

	try {
		// Always on top: the app's video pop-out window is, and would cover it otherwise - a browser ignores it
		win = await openPopout('cd4-drawing', { ...windowSize(), minWidth: MIN_WIDTH, minHeight: MIN_HEIGHT, alwaysOnTop: true })
	} catch (err) {
		logger.warning(`DrawingEditor: Could not open the drawing window - ${(err as Error).message}`)
		globalEvents.emit('CommonToast', { severity: 'warn', summary: t('mycalls.videoDrawingTitle'), detail: t('mycalls.videoDrawingWindowFailed') })
		close('its window did not open')
		return
	}
	if (closed) {
		win.close()
		return
	}

	popout.value = win
	win.document.title = `${t('mycalls.videoDrawingTitle')} - ${props.partyLabel}`
	stopSharingStyles = shareStyles(win.document)
	win.document.body.style.margin = '0'
	win.document.body.append(editor)
	win.addEventListener('pagehide', onWindowClosed, { once: true })
	win.addEventListener('resize', fitToWindow)
	win.addEventListener('keydown', onKey)
	// The window stays when the page goes - close it with the page
	window.addEventListener('pagehide', closeWindow, { once: true })
	fitToWindow()
	win.focus()

	// The window's own timer: the window is the one on screen while drawing, the page behind it may be throttled
	keepAlive = win.setInterval(paint, KEEP_ALIVE_MS)

	let shown: boolean

	try {
		sharing = SipClient.instance.shareDrawing(props.callId, canvas.captureStream(FRAME_RATE))
		shown = await sharing
	} catch (err) {
		logger.warning(`DrawingEditor: Could not show the drawing on call ${props.callId} - ${(err as Error).message}`)
		shown = false
	}
	sharing = undefined
	if (shown || closed) { return }

	// A refusal SipVideo reported (video rejected, could not start) has its toast already
	if (!videoCalls.calls[props.callId]?.error) {
		globalEvents.emit('CommonToast', { severity: 'warn', summary: t('mycalls.videoDrawingTitle'), detail: t('mycalls.videoDrawingFailed') })
	}
	close('the call could not show it')
}

// The drawing goes off the call by other means - hold, a change of other party, the call ending: the window closes
let wasShown = false

watch(() => videoCalls.calls[props.callId]?.source, source => {
	if (source === 'drawing') {
		wasShown = true
	} else if (wasShown) {
		close('it went off the call')
	}
})

onMounted(() => {
	void start()
})

onBeforeUnmount(() => {
	close('the view closed it')
})

// #endregion
</script>

<style scoped>
	.drawing-host {
		display: none;
	}

	.drawing-editor {
		display: flex;
		flex-direction: column;
		width: 100vw;
		height: 100vh;
		background: #161616;
		color: #eee;
		user-select: none;
	}

	.drawing-toolbar {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 6px 14px;
		min-height: 56px;
		padding: 8px 12px;
		background: var(--bg-color-dashboard-header, #2a2a2a);
	}

	.tool-group {
		display: flex;
		align-items: center;
		gap: 4px;
	}

	.tool {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 34px;
		height: 34px;
		border: 2px solid transparent;
		border-radius: 6px;
		background: rgba(255, 255, 255, 0.08);
		color: #eee;
		cursor: pointer;
	}

	.tool:disabled {
		opacity: 0.35;
		cursor: default;
	}

	.tool.selected {
		border-color: #fff;
		background: rgba(255, 255, 255, 0.22);
	}

	.text-glyph {
		font-weight: 700;
		font-size: 18px;
		font-family: Georgia, serif;
	}

	.swatch {
		width: 24px;
		height: 24px;
		border: 2px solid rgba(255, 255, 255, 0.35);
		border-radius: 50%;
		cursor: pointer;
	}

	.swatch.selected {
		border-color: #fff;
		box-shadow: 0 0 0 2px #161616, 0 0 0 4px #fff;
	}

	.dot {
		display: block;
		border-radius: 50%;
		box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5);
	}

	.hint {
		flex: 1 1 auto;
		font-size: 0.85rem;
		opacity: 0.75;
		text-align: right;
	}

	.drawing-stage {
		position: relative;
		flex: 1 1 auto;
		min-height: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
	}

	.paper {
		position: relative;
		flex: none;
	}

	.paper canvas {
		display: block;
		width: 100%;
		height: 100%;
		touch-action: none;
	}

	.paper canvas.pen {
		cursor: crosshair;
	}

	.paper canvas.text {
		cursor: text;
	}

	.text-entry {
		position: absolute;
		min-width: 12em;
		padding: 0;
		border: none;
		outline: 2px dashed rgba(255, 255, 255, 0.8);
		background: rgba(0, 0, 0, 0.25);
		line-height: 1;
	}

	.text-entry::placeholder {
		color: rgba(255, 255, 255, 0.6);
		font-weight: 400;
	}
</style>
